import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Tree from "react-d3-tree";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Download,
  FileImage,
  Loader2,
  Minus,
  Plus,
  Search,
} from "lucide-react";

function canonicalNet(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
}

function cloneTree(node) {
  if (!node) return null;
  return {
    ...node,
    children: (node.children || []).map(cloneTree),
  };
}

/** Mantiene nodos que cumplen filtro/búsqueda y el camino hasta la raíz. */
function pruneTree(node, netSlug, searchRaw) {
  const s = (searchRaw || "").trim().toLowerCase();
  const known = new Set(["discipulado_pastores", "adultos", "jovenes", "ninos"]);
  const netOk = (n) => {
    const c = canonicalNet(n.network_type);
    if (netSlug === "all") return true;
    if (netSlug === "sin_clasificar") return !known.has(c) || !n.network_type;
    return c === netSlug;
  };
  const searchOk = (n) => {
    if (!s) return true;
    const hay = [
      n.visible_name,
      n.leader_name,
      n.co_leader_name,
      n.red_label,
      n.location,
      n.address,
      n.parent_visible_name,
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(s);
  };

  function walk(n) {
    const kids = (n.children || []).map(walk).filter(Boolean);
    const selfKeep = netOk(n) && searchOk(n);
    if (selfKeep || kids.length) {
      return { ...n, children: kids };
    }
    return null;
  }

  return walk(cloneTree(node));
}

function pruneForest(roots, netSlug, searchRaw) {
  return (roots || []).map((r) => pruneTree(r, netSlug, searchRaw)).filter(Boolean);
}

function toD3(node) {
  if (!node) return null;
  const { children, ...rest } = node;
  return {
    name: node.visible_name || "—",
    attributes: { ...rest },
    children: (children || []).map(toD3),
  };
}

const CARD_BY_NET = {
  discipulado_pastores:
    "border-violet-700 bg-gradient-to-br from-violet-950 via-violet-900 to-amber-800 text-amber-50 shadow-lg shadow-violet-900/30",
  adultos: "border-blue-500 bg-gradient-to-br from-white to-blue-50 text-slate-900 shadow-md",
  jovenes: "border-orange-500 bg-gradient-to-br from-white to-orange-50 text-slate-900 shadow-md",
  ninos: "border-emerald-600 bg-gradient-to-br from-white to-emerald-50 text-slate-900 shadow-md",
  sin_clasificar: "border-slate-400 bg-gradient-to-br from-white to-slate-100 text-slate-900 shadow-md",
};

function cardClass(networkType) {
  const c = canonicalNet(networkType);
  return CARD_BY_NET[c] || CARD_BY_NET.sin_clasificar;
}

/** Dimensiones por defecto según ventana (modal organigrama). */
function getViewportTreeDims() {
  if (typeof window === "undefined") {
    return { width: 1200, height: 620 };
  }
  return {
    width: Math.max(400, window.innerWidth - 120),
    height: Math.max(420, window.innerHeight - 220),
  };
}

/** Prioriza el rect del contenedor; si aún no tiene tamaño útil, usa el viewport. */
function measureTreeContainer(el) {
  const r = el.getBoundingClientRect();
  const w = Math.round(r.width);
  const h = Math.round(r.height);
  if (w >= 320 && h >= 320) {
    return { width: Math.max(400, w), height: Math.max(420, h) };
  }
  return getViewportTreeDims();
}

/**
 * @param {import("axios").AxiosInstance} api
 * @param {boolean} open
 * @param {() => void} onClose
 */
export function CellOrganizationChart({ api, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [payload, setPayload] = useState(null);
  const [filterNet, setFilterNet] = useState("all");
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(0.55);
  const [translate, setTranslate] = useState({ x: 400, y: 70 });
  const [treeDims, setTreeDims] = useState(getViewportTreeDims);
  const treeWrapRef = useRef(null);
  const captureRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await api.get("/cells/organization-chart");
        if (!cancelled) setPayload(res.data);
      } catch (e) {
        if (!cancelled) {
          const d = e.response?.data?.detail;
          setErr(typeof d === "string" ? d : "No se pudo cargar el organigrama");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, open]);

  useLayoutEffect(() => {
    if (!open || !treeWrapRef.current) return;
    const el = treeWrapRef.current;
    const apply = () => {
      const { width: w, height: h } = measureTreeContainer(el);
      setTreeDims({ width: w, height: h });
      setTranslate({ x: Math.max(w / 2, 200), y: 80 });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, payload]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (treeWrapRef.current) {
        const { width: w, height: h } = measureTreeContainer(treeWrapRef.current);
        setTreeDims({ width: w, height: h });
        setTranslate((prev) => ({ x: Math.max(w / 2, 200), y: prev.y }));
      } else {
        setTreeDims(getViewportTreeDims());
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const filteredRoots = useMemo(() => {
    if (!payload?.tree?.length) return [];
    return pruneForest(payload.tree, filterNet, search);
  }, [payload, filterNet, search]);

  const d3Data = useMemo(() => {
    if (!filteredRoots.length) return null;
    if (filteredRoots.length === 1) return toD3(filteredRoots[0]);
    return {
      name: "ICJEME",
      attributes: { id: "__multi__", visible_name: "ICJEME", red_label: "—" },
      children: filteredRoots.map(toD3),
    };
  }, [filteredRoots]);

  const exportPng = useCallback(async () => {
    const el = captureRef.current;
    if (!el) return;
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#f1f5f9",
      logging: false,
    });
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `organigrama_celulas_${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
  }, []);

  const exportPdf = useCallback(async () => {
    const el = captureRef.current;
    if (!el) return;
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#f1f5f9",
      logging: false,
    });
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const titleH = 18;
    pdf.setFontSize(15);
    pdf.setTextColor(30, 41, 59);
    pdf.text("Organigrama General de Células ICJEME", margin, margin + 6);
    pdf.setFontSize(9);
    pdf.setTextColor(71, 85, 105);
    pdf.text(`Generado: ${new Date().toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}`, margin, margin + 12);

    const usableW = pw - 2 * margin;
    const usableH = ph - 2 * margin - titleH;
    const imgW = usableW;
    const imgH = (canvas.height * imgW) / canvas.width;
    const imgData = canvas.toDataURL("image/png", 1.0);

    if (imgH <= usableH) {
      pdf.addImage(imgData, "PNG", margin, margin + titleH, imgW, imgH, undefined, "FAST");
    } else {
      let y = 0;
      let page = 0;
      const sliceH = (usableH * canvas.width) / imgW;
      while (y < canvas.height) {
        if (page > 0) pdf.addPage();
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        const h = Math.min(sliceH, canvas.height - y);
        sliceCanvas.height = h;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        const part = sliceCanvas.toDataURL("image/png", 1.0);
        const partH = (h * imgW) / canvas.width;
        pdf.addImage(part, "PNG", margin, margin + titleH, imgW, partH, undefined, "FAST");
        y += h;
        page += 1;
      }
    }
    pdf.save(`organigrama_celulas_${new Date().toISOString().slice(0, 10)}.pdf`);
  }, []);

  const summary = payload?.summary;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[94vh] w-[min(96vw,1400px)] max-w-[96vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[96vw]">
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 pr-12 text-left">
          <DialogTitle className="text-xl font-bold text-slate-900">
            Organigrama de Células ICJEME
          </DialogTitle>
          <p className="text-sm text-slate-600">
            Jerarquía según enlace de red (parent_cell_id). Raíz: Discipulado Pastores.
          </p>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-100">
          {loading && (
            <div className="flex flex-1 items-center justify-center gap-2 py-20 text-slate-600">
              <Loader2 className="h-8 w-8 animate-spin" />
              Cargando organigrama…
            </div>
          )}
          {err && !loading && (
            <div className="p-6 text-center text-red-700">{err}</div>
          )}
          {!loading && !err && (
            <>
              {summary && (
                <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:grid-cols-4 lg:grid-cols-7">
                  {[
                    ["Total células", summary.total_cells],
                    ["Discipulado Pastores", summary.total_discipulado_pastores],
                    ["Adultos", summary.total_adultos],
                    ["Jóvenes", summary.total_jovenes],
                    ["Niños", summary.total_ninos],
                    ["Sin clasificar", summary.total_sin_clasificar],
                    ["Células hijas (enlaces)", summary.total_celulas_hijas],
                    ["Profundidad máx.", summary.max_depth],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-center shadow-sm"
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{k}</div>
                      <div className="text-lg font-bold tabular-nums text-slate-900">{v}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex shrink-0 flex-wrap items-end gap-2 border-b border-slate-200 bg-white px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {[
                    ["all", "Todas"],
                    ["discipulado_pastores", "Discipulado Pastores"],
                    ["adultos", "Adultos"],
                    ["jovenes", "Jóvenes"],
                    ["ninos", "Niños"],
                  ].map(([val, lab]) => (
                    <Button
                      key={val}
                      type="button"
                      size="sm"
                      variant={filterNet === val ? "default" : "outline"}
                      className="h-8"
                      onClick={() => setFilterNet(val)}
                    >
                      {lab}
                    </Button>
                  ))}
                </div>
                <div className="relative min-w-[200px] flex-1">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    className="h-9 pl-8"
                    placeholder="Buscar: líder, red, zona, enlace…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label className="sr-only">Zoom</Label>
                  <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => setZoom((z) => Math.max(0.12, z - 0.08))}>
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center text-xs tabular-nums text-slate-600">{Math.round(zoom * 100)}%</span>
                  <Button type="button" size="icon" variant="outline" className="h-9 w-9" onClick={() => setZoom((z) => Math.min(1.3, z + 0.08))}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Button type="button" variant="secondary" size="sm" className="gap-1" onClick={exportPdf}>
                  <Download className="h-4 w-4" />
                  PDF
                </Button>
                <Button type="button" variant="secondary" size="sm" className="gap-1" onClick={exportPng}>
                  <FileImage className="h-4 w-4" />
                  PNG
                </Button>
              </div>

              <div ref={captureRef} className="min-h-0 flex-1 overflow-auto bg-gradient-to-b from-slate-100 to-slate-200/90">
                <div
                  ref={treeWrapRef}
                  className="relative w-full min-h-[480px]"
                  style={{
                    height: "min(72vh, calc(100dvh - 240px))",
                    minHeight: 480,
                  }}
                >
                  {!d3Data && (
                    <div className="flex h-96 items-center justify-center text-slate-500">
                      Sin resultados con el filtro o búsqueda actual.
                    </div>
                  )}
                  {d3Data && (
                    <Tree
                      key={`${filterNet}-${search}-${filteredRoots.length}`}
                      data={d3Data}
                      orientation="vertical"
                      dimensions={treeDims}
                      translate={translate}
                      zoom={zoom}
                      zoomable
                      draggable
                      hasInteractiveNodes
                      scaleExtent={{ min: 0.1, max: 1.5 }}
                      nodeSize={{ x: 300, y: 230 }}
                      separation={{ siblings: 1.05, nonSiblings: 1.15 }}
                      collapsible
                      pathFunc="step"
                      styles={{
                        links: { stroke: "#64748b", strokeWidth: 2 },
                      }}
                      renderCustomNodeElement={({ nodeDatum, toggleNode }) => {
                        const a = nodeDatum.attributes || {};
                        const net = a.network_type;
                        return (
                          <g>
                            <foreignObject width={280} height={200} x={-140} y={-100}>
                              <div
                                xmlns="http://www.w3.org/1999/xhtml"
                                className={`flex h-full flex-col rounded-xl border-2 p-2.5 text-left ${cardClass(net)}`}
                              >
                                <div className="flex items-start justify-between gap-1">
                                  <div className="min-w-0 flex-1 text-xs font-bold leading-tight">
                                    {a.visible_name || nodeDatum.name}
                                  </div>
                                  {(nodeDatum.children?.length > 0 || a.children_count > 0) && (
                                    <button
                                      type="button"
                                      className="shrink-0 rounded border border-white/30 bg-black/10 px-1.5 py-0.5 text-[10px] font-medium text-inherit hover:bg-black/20"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        toggleNode();
                                      }}
                                    >
                                      ±
                                    </button>
                                  )}
                                </div>
                                <div className="mt-1 text-[10px] font-medium opacity-90">{a.red_label || "—"}</div>
                                <div className="mt-1 space-y-0.5 text-[10px] leading-snug opacity-95">
                                  <div>
                                    <span className="font-semibold">Líder:</span> {a.leader_name || "—"}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Co-líder:</span> {a.co_leader_name || "—"}
                                  </div>
                                  <div>
                                    <span className="font-semibold">Zona:</span> {a.location || "—"}
                                  </div>
                                  <div className="flex gap-2 pt-0.5">
                                    <span>Miembros: {a.members_count ?? 0}</span>
                                    <span>Hijas: {a.children_count ?? 0}</span>
                                  </div>
                                </div>
                              </div>
                            </foreignObject>
                          </g>
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
